import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Search, CheckCircle, Mail, Users, Cloud, Calculator, FolderOpen, Video, Lock, AlertTriangle, Wifi, FileText, ChevronRight, GraduationCap, Download, Monitor, Laptop, Smartphone, Settings, Wrench, Edit, Bell, Layout, Folder, Zap, Calendar, CheckSquare, Paperclip, HardDrive, Shield, Layers, MessageSquare, Puzzle, Globe, RefreshCw, Users2, Link2, Bot, FileEdit, MessageCircle, Eye, Fingerprint, AppWindow, Briefcase, ArrowRight, ExternalLink, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { mockGuides, mockCategories } from '../mock';
import Breadcrumb from './Breadcrumb';
import Office365Page from './Office365Page';
import GoogleWorkspacePage from './GoogleWorkspacePage';
import Footer from './Footer';

const CategoryPage = ({ category, setCurrentSection, searchResults, searchQuery, initialGuide }) => {
  const navigate = useNavigate();
  const [selectedGuide, setSelectedGuide] = useState(initialGuide || null);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [completedGuides, setCompletedGuides] = useState(new Set());

  const categoryData = mockCategories.find(c => c.id === category);
  const guides = (mockGuides[category] || []).filter(guide => !guide.isSubPage);
  const allGuides = mockGuides[category] || []; // Include all guides for internal navigation

  // Dynamic banner gradient based on category
  const getBannerGradient = () => {
    if (!categoryData) return 'from-indigo-600 via-purple-600 to-cyan-600';
    
    switch (categoryData.theme) {
      case 'blue':
        return 'from-blue-600 via-blue-700 to-indigo-600';
      case 'emerald':
        return 'from-green-600 via-emerald-600 to-teal-600';
      case 'orange':
        return 'from-red-600 via-orange-600 to-yellow-600';
      case 'purple':
        return 'from-purple-600 via-pink-600 to-rose-600';
      case 'teal':
        return 'from-teal-600 via-cyan-600 to-blue-600';
      case 'indigo':
        return 'from-indigo-600 via-purple-600 to-pink-600';
      default:
        return 'from-indigo-600 via-purple-600 to-cyan-600';
    }
  };

  // Dynamic text color for banner
  const getBannerTextColor = () => {
    if (!categoryData) return 'text-indigo-100';
    
    switch (categoryData.theme) {
      case 'blue':
        return 'text-blue-100';
      case 'emerald':
        return 'text-green-100';
      case 'orange':
        return 'text-red-100';
      case 'purple':
        return 'text-purple-100';
      case 'teal':
        return 'text-teal-100';
      case 'indigo':
        return 'text-indigo-100';
      default:
        return 'text-indigo-100';
    }
  };

  // Dynamic background color for page
  const getPageBackground = () => {
    if (!categoryData) return 'from-indigo-50 via-white to-cyan-50';
    
    switch (categoryData.theme) {
      case 'blue':
        return 'from-blue-50 via-white to-indigo-50';
      case 'emerald':
        return 'from-green-50 via-white to-emerald-50';
      case 'orange':
        return 'from-red-50 via-white to-orange-50';
      case 'purple':
        return 'from-purple-50 via-white to-pink-50';
      case 'teal':
        return 'from-teal-50 via-white to-cyan-50';
      case 'indigo':
        return 'from-indigo-50 via-white to-purple-50';
      default:
        return 'from-indigo-50 via-white to-cyan-50';
    }
  };

  // Handle navigation to guide from internal links
  React.useEffect(() => {
    const handleNavigateToGuide = (event) => {
      const targetGuide = event.detail;
      setSelectedGuide(targetGuide);
    };

    window.addEventListener('navigateToGuide', handleNavigateToGuide);
    return () => window.removeEventListener('navigateToGuide', handleNavigateToGuide);
  }, []);

  // Filter guides based on search
  const filteredGuides = useMemo(() => {
    if (searchResults && searchQuery) {
      return searchResults.filter(guide => 
        guides.some(g => g.id === guide.id)
      );
    }
    
    if (localSearchQuery) {
      return guides.filter(guide => 
        guide.title.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
        guide.description.toLowerCase().includes(localSearchQuery.toLowerCase()) ||
        guide.tags.some(tag => tag.toLowerCase().includes(localSearchQuery.toLowerCase()))
      );
    }
    
    return guides;
  }, [guides, searchResults, searchQuery, localSearchQuery]);

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'Débutant': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Intermédiaire': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Essentiel': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Mail, Users, Cloud, Calculator, FolderOpen, Video, Lock, AlertTriangle, Wifi, FileText, GraduationCap, Download, Monitor, Laptop, Smartphone, Settings, Wrench, Edit, Bell, Layout, Folder, Zap, Calendar, CheckSquare, Paperclip, HardDrive, Shield, Layers, MessageSquare, Puzzle, Globe, RefreshCw, Users2, Link2, Bot, FileEdit, MessageCircle, Eye, Fingerprint, AppWindow, Briefcase
    };
    return icons[iconName] || FileText;
  };

  const markAsCompleted = (guideId) => {
    const newCompleted = new Set(completedGuides);
    newCompleted.add(guideId);
    setCompletedGuides(newCompleted);
  };

  if (selectedGuide) {
    return (
      <GuideDetail 
        guide={selectedGuide} 
        onBack={() => setSelectedGuide(null)}
        onComplete={() => markAsCompleted(selectedGuide.id)}
        isCompleted={completedGuides.has(selectedGuide.id)}
        categoryData={categoryData}
      />
    );
  }

  // Use specialized Office365Page for office365 category
  if (category === 'office365' && !selectedGuide) {
    return (
      <Office365Page 
        setCurrentSection={setCurrentSection}
        onSelectGuide={setSelectedGuide}
        guides={guides}
      />
    );
  }

  // Use specialized GoogleWorkspacePage for google-workspace category
  if (category === 'google-workspace' && !selectedGuide) {
    return (
      <GoogleWorkspacePage 
        setCurrentSection={setCurrentSection}
        onSelectGuide={setSelectedGuide}
        guides={guides}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Full Width Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 p-4 rounded-2xl mr-6 backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
              {React.createElement(getIcon(categoryData?.icon), { 
                className: "h-16 w-16 text-white" 
              })}
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">{categoryData?.name}</h1>
              <p className="text-indigo-100 text-xl md:text-2xl max-w-4xl leading-relaxed">{categoryData?.description}</p>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-pink-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-cyan-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => setCurrentSection('home')}
            className="hover:bg-white hover:bg-opacity-60 transition-all duration-200 text-gray-600 hover:text-indigo-600 backdrop-blur-sm"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour à l'accueil
          </Button>
        </div>

        {/* Breadcrumb */}
        <Breadcrumb 
          items={[{ id: category, label: categoryData?.name }]}
          onNavigate={setCurrentSection}
        />

        {/* Search Section */}
        <div className="mb-12">
          {/* Modern Search */}
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
              <Search className="h-6 w-6 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder={`Rechercher dans ${categoryData?.name}...`}
              className="w-full pl-16 pr-6 py-4 bg-white bg-opacity-70 backdrop-blur-sm border-0 rounded-2xl text-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-opacity-90 transition-all duration-300"
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Search Results Info */}
        {(searchQuery && searchResults) && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 shadow-lg">
              <p className="text-white font-bold text-lg flex items-center">
                <Search className="h-5 w-5 mr-3" />
                {searchResults.length} résultat{searchResults.length !== 1 ? 's' : ''} pour "{searchQuery}" dans {categoryData?.name}
              </p>
            </div>
          </div>
        )}

        {/* Modern Guides Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGuides.map((guide) => {
            const IconComponent = getIcon(guide.icon);
            const isCompleted = completedGuides.has(guide.id);
            
            return (
              <Card 
                key={guide.id} 
                className="group cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-0 overflow-hidden bg-white rounded-2xl"
                onClick={() => setSelectedGuide(guide)}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                
                <CardHeader className="pb-4 relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-2xl bg-gradient-to-br ${
                      guide.difficulty === 'Débutant' ? 'from-green-500 to-emerald-600' :
                      guide.difficulty === 'Intermédiaire' ? 'from-yellow-500 to-orange-600' :
                      'from-red-500 to-pink-600'
                    } group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {isCompleted && (
                        <div className="bg-green-100 p-1 rounded-full">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <CardTitle className="text-xl font-bold text-gray-900 mb-4 group-hover:text-indigo-600 transition-colors duration-200">
                    {guide.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="pt-0 relative z-10">
                  <CardDescription className="text-gray-600 mb-6 leading-relaxed">
                    {guide.description}
                  </CardDescription>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {guide.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <Button 
                    variant="ghost"
                    className="w-full group-hover:bg-gradient-to-r group-hover:from-indigo-500 group-hover:to-purple-600 group-hover:text-white font-bold transition-all duration-300 rounded-xl py-2"
                  >
                    Consulter le guide
                    <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </CardContent>
                
                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000"></div>
              </Card>
            );
          })}
        </div>

        {/* No Results */}
        {filteredGuides.length === 0 && (
          <div className="text-center py-16">
            <div className="bg-white rounded-xl shadow-sm p-12 max-w-md mx-auto border border-gray-200">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun résultat trouvé</h3>
              <p className="text-gray-600">
                Essayez avec d'autres mots-clés ou explorez une autre catégorie
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// Guide Detail Component
const GuideDetail = ({ guide, onBack, onComplete, isCompleted, categoryData }) => {
  const IconComponent = getIcon(guide.icon);
  
  // Helper function to get parent guide title
  const getParentGuideTitle = (parentGuideId) => {
    const allGuides = mockGuides[categoryData?.id] || [];
    const parentGuide = allGuides.find(g => g.id === parentGuideId);
    return parentGuide ? parentGuide.title : 'Guide parent';
  };
  
  // Function to handle internal links with line breaks
  const handleInternalLink = (content) => {
    // Split content by bullet points and process each one
    const lines = content.split('\n');
    let processedContent = '';
    
    lines.forEach((line, index) => {
      if (line.trim().startsWith('• [') || (line.trim().startsWith('• ') && line.includes('['))) {
        // Handle bullet points with links - put each on separate line
        let processedLine = line;
        
        // FIRST: Make quoted text bold BEFORE processing links
        processedLine = processedLine.replace(
          /"([^"]+)"/g,
          '<strong>"$1"</strong>'
        );
        
        // THEN: Replace all links in the line (support multiple links per bullet point)
        processedLine = processedLine.replace(
          /\[([^\]]+)\]\(([^)]+)\)/g, 
          (match, linkText, linkUrl) => {
            // Check if it's an external link (starts with http/https)
            if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
              return `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800 font-medium">${linkText}</a>`;
            } else {
              // Internal link
              return `<span class="internal-link text-blue-600 underline cursor-pointer hover:text-blue-800 font-medium" data-guide="${linkUrl}">${linkText}</span>`;
            }
          }
        );
        
        // Convert bullet point to div with bullet
        processedLine = processedLine.replace(
          /^• (.+)$/,
          '<div class="mb-3 flex items-start"><span class="text-blue-500 mr-3">•</span><span class="text-gray-700">$1</span></div>'
        );
        
        processedContent += processedLine;
      } else if (line.trim().startsWith('• ')) {
        // Handle regular bullet points - put each on separate line
        let processedLine = line;
        
        // FIRST: Make quoted text bold BEFORE other processing
        processedLine = processedLine.replace(
          /"([^"]+)"/g,
          '<strong>"$1"</strong>'
        );
        
        // THEN: Convert bullet point
        processedLine = processedLine.replace(
          /• (.+)/g, 
          '<div class="mb-3 flex items-start"><span class="text-gray-500 mr-3">•</span><span class="text-gray-700">$1</span></div>'
        );
        
        processedContent += processedLine;
      } else if (line.trim()) {
        // Handle regular text - also check for links in regular text
        let processedLine = line.trim();
        
        // FIRST: Make quoted text bold BEFORE processing links
        processedLine = processedLine.replace(
          /"([^"]+)"/g,
          '<strong>"$1"</strong>'
        );
        
        // THEN: Replace links in regular text
        processedLine = processedLine.replace(
          /\[([^\]]+)\]\(([^)]+)\)/g, 
          (match, linkText, linkUrl) => {
            if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
              return `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800 font-medium">${linkText}</a>`;
            } else {
              return `<span class="internal-link text-blue-600 underline cursor-pointer hover:text-blue-800 font-medium" data-guide="${linkUrl}">${linkText}</span>`;
            }
          }
        );
        
        processedContent += `<p class="mb-4 text-gray-700 leading-relaxed">${processedLine}</p>`;
      } else {
        // Empty line
        processedContent += '<br>';
      }
    });
    
    return processedContent;
  };

  // Function to handle click on internal links
  const handleLinkClick = (e) => {
    if (e.target.classList.contains('internal-link')) {
      const guideId = e.target.getAttribute('data-guide');
      if (guideId) {
        // Find the guide by ID and navigate to it
        const allGuides = [
          ...mockGuides.office365, 
          ...mockGuides['google-workspace'], 
          ...mockGuides.security,
          ...mockGuides.mobile,
          ...mockGuides['operating-systems']
        ];
        const targetGuide = allGuides.find(g => g.id === guideId);
        if (targetGuide) {
          // Determine the correct category for navigation
          let targetCategory = 'office365'; // default
          
          if (mockGuides.office365.some(g => g.id === guideId)) {
            targetCategory = 'office365';
          } else if (mockGuides['google-workspace'].some(g => g.id === guideId)) {
            targetCategory = 'google-workspace';
          } else if (mockGuides.security.some(g => g.id === guideId)) {
            targetCategory = 'security';
          } else if (mockGuides.mobile.some(g => g.id === guideId)) {
            targetCategory = 'mobile';
          } else if (mockGuides['operating-systems'].some(g => g.id === guideId)) {
            targetCategory = 'operating-systems';
          }
          
          // Navigate using window.location for internal links
          window.location.href = `/${targetCategory}/guides/${guideId}`;
        }
      }
    }
  };

  // Add event listener for internal links
  React.useEffect(() => {
    document.addEventListener('click', handleLinkClick);
    return () => document.removeEventListener('click', handleLinkClick);
  }, []);
  
  return (
    <div className={`min-h-screen bg-gradient-to-br ${
      categoryData?.theme === 'blue' ? 'from-blue-50 via-white to-indigo-50' :
      categoryData?.theme === 'emerald' ? 'from-green-50 via-white to-emerald-50' :
      categoryData?.theme === 'orange' ? 'from-red-50 via-white to-orange-50' :
      categoryData?.theme === 'purple' ? 'from-purple-50 via-white to-pink-50' :
      categoryData?.theme === 'teal' ? 'from-teal-50 via-white to-cyan-50' :
      categoryData?.theme === 'indigo' ? 'from-indigo-50 via-white to-purple-50' :
      'from-indigo-50 via-white to-cyan-50'
    }`}>
      {/* Full Width Guide Banner */}
      <section className={`relative overflow-hidden bg-gradient-to-r ${
        categoryData?.theme === 'blue' ? 'from-blue-600 via-blue-700 to-indigo-600' :
        categoryData?.theme === 'emerald' ? 'from-green-600 via-emerald-600 to-teal-600' :
        categoryData?.theme === 'orange' ? 'from-red-600 via-orange-600 to-yellow-600' :
        categoryData?.theme === 'purple' ? 'from-purple-600 via-pink-600 to-rose-600' :
        categoryData?.theme === 'teal' ? 'from-teal-600 via-cyan-600 to-blue-600' :
        categoryData?.theme === 'indigo' ? 'from-indigo-600 via-purple-600 to-pink-600' :
        'from-indigo-600 via-purple-600 to-cyan-600'
      }`}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="bg-white bg-opacity-20 p-6 rounded-3xl backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
              <IconComponent className="h-20 w-20 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-4 mb-6">
                <Badge className="bg-white bg-opacity-20 text-white font-bold px-6 py-3 text-base backdrop-blur-sm rounded-full">
                  {categoryData?.name}
                </Badge>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                {guide.title}
              </h1>
              <p className={`${
                categoryData?.theme === 'blue' ? 'text-blue-100' :
                categoryData?.theme === 'emerald' ? 'text-green-100' :
                categoryData?.theme === 'orange' ? 'text-red-100' :
                categoryData?.theme === 'purple' ? 'text-purple-100' :
                categoryData?.theme === 'teal' ? 'text-teal-100' :
                categoryData?.theme === 'indigo' ? 'text-indigo-100' :
                'text-indigo-100'
              } text-xl md:text-2xl leading-relaxed max-w-5xl mb-8`}>
                {guide.description}
              </p>
              
              <div className="flex flex-wrap gap-3">
                {guide.tags.map(tag => (
                  <span key={tag} className="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm text-white rounded-full text-base font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-pink-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 right-1/4 w-12 h-12 bg-cyan-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb 
          items={[
            { id: categoryData.id, label: categoryData?.name },
            ...(guide.isSubPage && guide.parentGuide ? [
              { id: guide.parentGuide, label: getParentGuideTitle(guide.parentGuide) }
            ] : []),
            { id: guide.id, label: guide.title }
          ]}
        />

        {/* Auto-detect parent guide for procedures */}
        {(() => {
          if (guide.isSubPage && !guide.parentGuide) {
            if (guide.id.startsWith('outlook-')) {
              guide.parentGuide = 'outlook-basics';
            } else if (guide.id.startsWith('word-')) {
              guide.parentGuide = 'word-document-mastery';
            } else if (guide.id.startsWith('excel-')) {
              guide.parentGuide = 'excel-data-mastery';
            } else if (guide.id.startsWith('powerpoint-')) {
              guide.parentGuide = 'powerpoint-presentation-mastery';
            } else if (guide.id.startsWith('teams-')) {
              guide.parentGuide = 'teams-collaboration';
            } else if (guide.id.startsWith('onedrive-')) {
              guide.parentGuide = 'onedrive-storage';
            } else if (guide.id.startsWith('sharepoint-')) {
              guide.parentGuide = 'sharepoint-collaboration';
            } else if (guide.id.startsWith('copilot-')) {
              guide.parentGuide = 'copilot-productivity';
            } else if (guide.id.startsWith('gmail-')) {
              guide.parentGuide = 'gmail-organization';
            } else if (guide.id.startsWith('docs-')) {
              guide.parentGuide = 'docs-collaboration';
            } else if (guide.id.startsWith('sheets-')) {
              guide.parentGuide = 'sheets-data-analysis';
            } else if (guide.id.startsWith('meet-')) {
              guide.parentGuide = 'meet-videoconference';
            } else if (guide.id.startsWith('password-') || guide.id.startsWith('2fa-') || guide.id.startsWith('master-')) {
              guide.parentGuide = 'password-security';
            } else if (guide.id.startsWith('iphone-') || guide.id.startsWith('ipad-') || guide.id.startsWith('icloud-') || guide.id.startsWith('ios-') || guide.id.startsWith('mac-') || guide.id.startsWith('apple-')) {
              guide.parentGuide = 'apple-ecosystem-guide';
            } else if (guide.id.startsWith('android-')) {
              guide.parentGuide = 'android-ecosystem-guide';
            }
          }
          return null;
        })()}

        {/* Introduction Section */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8">
          <div className={`bg-gradient-to-r ${
            categoryData?.theme === 'blue' ? 'from-blue-500 to-indigo-600' :
            categoryData?.theme === 'emerald' ? 'from-green-500 to-emerald-600' :
            categoryData?.theme === 'orange' ? 'from-red-500 to-orange-600' :
            categoryData?.theme === 'purple' ? 'from-purple-500 to-pink-600' :
            categoryData?.theme === 'teal' ? 'from-teal-500 to-cyan-600' :
            categoryData?.theme === 'indigo' ? 'from-indigo-500 to-purple-600' :
            'from-indigo-500 to-purple-600'
          } text-white p-8`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <FileText className="h-8 w-8 mr-3" />
              Introduction
            </h2>
            <p className="text-lg leading-relaxed opacity-90">
              {guide.content.introduction}
            </p>
          </div>
        </div>

        {/* Sections Layout - Clean and Linear */}
        <div className="space-y-8">
          {guide.content.sections.map((section, index) => {
            const SectionIcon = getIcon(section.sectionIcon || 'FileText');
            
            return (
              <div key={index} className="mb-12">
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-3 rounded-xl ${
                    categoryData?.theme === 'blue' ? 'bg-blue-500' :
                    categoryData?.theme === 'emerald' ? 'bg-green-500' :
                    categoryData?.theme === 'orange' ? 'bg-red-500' :
                    categoryData?.theme === 'purple' ? 'bg-purple-500' :
                    categoryData?.theme === 'teal' ? 'bg-teal-500' :
                    categoryData?.theme === 'indigo' ? 'bg-indigo-500' :
                    'bg-indigo-500'
                  } shadow-lg`}>
                    <SectionIcon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                </div>
                
                {/* Section Content */}
                <div className={`${section.sectionColor || 
                  (categoryData?.theme === 'blue' ? 'bg-blue-50 border-l-4 border-l-blue-500' :
                   categoryData?.theme === 'emerald' ? 'bg-green-50 border-l-4 border-l-green-500' :
                   categoryData?.theme === 'orange' ? 'bg-red-50 border-l-4 border-l-orange-500' :
                   categoryData?.theme === 'purple' ? 'bg-purple-50 border-l-4 border-l-purple-500' :
                   categoryData?.theme === 'teal' ? 'bg-teal-50 border-l-4 border-l-teal-500' :
                   categoryData?.theme === 'indigo' ? 'bg-indigo-50 border-l-4 border-l-indigo-500' :
                   'bg-gray-50 border-l-4 border-l-gray-500')
                } rounded-xl p-8 shadow-lg`}>
                  <div 
                    className="text-gray-700 leading-relaxed prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: handleInternalLink(section.content)
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Enhanced Conclusion */}
        {guide.content.conclusion && (
          <Card className="mt-8 border-0 shadow-xl overflow-hidden">
            <div className={`bg-gradient-to-r ${
              categoryData?.theme === 'blue' ? 'from-blue-500 to-indigo-600' :
              categoryData?.theme === 'emerald' ? 'from-green-500 to-emerald-600' :
              categoryData?.theme === 'orange' ? 'from-red-500 to-orange-600' :
              categoryData?.theme === 'purple' ? 'from-purple-500 to-pink-600' :
              categoryData?.theme === 'teal' ? 'from-teal-500 to-cyan-600' :
              categoryData?.theme === 'indigo' ? 'from-indigo-500 to-purple-600' :
              'from-indigo-500 to-purple-600'
            } text-white p-8`}>
              <h3 className="text-2xl font-bold mb-4 flex items-center">
                <CheckCircle className="h-8 w-8 mr-3" />
                Conclusion
              </h3>
              <p className="text-lg leading-relaxed opacity-90">
                {guide.content.conclusion}
              </p>
            </div>
          </Card>
        )}

        {/* Section Besoin d'assistance - Seulement pour les procédures (sous-pages) */}
        {guide.isSubPage && (
          <section className="mt-12 mb-8">
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 sm:p-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4">
                    <HelpCircle className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4">
                    Besoin d'une assistance ?
                  </h3>
                  <p className="text-base sm:text-lg text-indigo-100 mb-6 leading-relaxed">
                    Notre équipe d'experts est là pour vous accompagner. Ouvrez un ticket de support pour une aide personnalisée.
                  </p>
                  <a 
                    href={`/contact?type=support&page=${encodeURIComponent(guide.title)}`}
                    className="inline-flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full backdrop-blur-sm transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <HelpCircle className="h-5 w-5 mr-3" />
                    Ouvrir un ticket de support
                    <ExternalLink className="ml-3 h-5 w-5" />
                  </a>
                </div>
              </div>
            </Card>
          </section>
        )}

        {/* Section Suggestions pour guides principaux */}
        {!guide.isSubPage && (
          <section className="mt-12 mb-8">
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 sm:p-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4">
                    <MessageSquare className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-4">
                    Vous ne trouvez pas la procédure ou avez une suggestion à faire ?
                  </h3>
                  <p className="text-base sm:text-lg text-blue-100 mb-6 leading-relaxed">
                    Aidez-nous à améliorer nos guides ! Signalez une procédure manquante ou partagez vos suggestions d'amélioration.
                  </p>
                  <a 
                    href={`/contact?type=suggestion&page=${encodeURIComponent(guide.title)}`}
                    className="inline-flex items-center bg-white bg-opacity-20 hover:bg-opacity-30 text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full backdrop-blur-sm transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <MessageSquare className="h-5 w-5 mr-3" />
                    Faire une suggestion
                    <ExternalLink className="ml-3 h-5 w-5" />
                  </a>
                </div>
              </div>
            </Card>
          </section>
        )}

        {/* Back Button Enhanced */}
        <div className="mt-12 text-center">
          <Button 
            onClick={() => {
              // Check if we came from a parent guide based on URL structure
              const currentPath = window.location.pathname;
              const pathParts = currentPath.split('/');
              
              if (pathParts.length >= 4 && pathParts[2] === 'guides') {
                // We're in a procedure, go back to the parent guide
                const parentGuideUrl = `/${pathParts[1]}/guides/${guide.parentGuide || pathParts[3]}`;
                window.history.back();
              } else {
                // Default behavior - go back to category page
                if (onBack) {
                  onBack();
                } else {
                  window.history.back();
                }
              }
            }}
            className={`bg-gradient-to-r ${
              categoryData?.theme === 'blue' ? 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700' :
              categoryData?.theme === 'emerald' ? 'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' :
              categoryData?.theme === 'orange' ? 'from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700' :
              categoryData?.theme === 'purple' ? 'from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' :
              categoryData?.theme === 'teal' ? 'from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700' :
              categoryData?.theme === 'indigo' ? 'from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700' :
              'from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
            } text-white font-bold px-8 py-4 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-200`}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {guide.isSubPage ? 'Retour au guide' : 'Retour aux guides'}
          </Button>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

// Helper functions
const getDifficultyColor = (difficulty) => {
  switch(difficulty) {
    case 'Débutant': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Intermédiaire': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'Essentiel': return 'bg-red-50 text-red-700 border-red-200';
    default: return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const getIcon = (iconName) => {
  const icons = {
    Mail, Users, Cloud, Calculator, FolderOpen, Video, Lock, AlertTriangle, Wifi, FileText, GraduationCap, Download, Monitor, Laptop, Smartphone, Settings, Wrench, Edit, Bell, Layout, Folder, Zap, Calendar, CheckSquare, Paperclip, HardDrive, Shield, Layers, MessageSquare, Puzzle, Globe, RefreshCw, Users2, Link2, Bot, FileEdit, MessageCircle, Eye, Fingerprint, AppWindow, Briefcase
  };
  return icons[iconName] || FileText;
};

export default CategoryPage;